import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import AdminNavbar from './AdminNavbar';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiSearch, FiFilter, FiMoreVertical, FiEdit, FiTrash2, FiMail, FiPhone } = FiIcons;

const AdminUsers = () => {
  const [users] = useState([
    {
      id: 1,
      name: 'John Doe',
      email: 'john.doe@company.com',
      plan: 'Professional',
      monitors: 8,
      status: 'active',
      joinDate: '2024-01-15',
      lastActive: '2 hours ago'
    },
    {
      id: 2,
      name: 'Jane Smith',
      email: 'jane.smith@business.com',
      plan: 'Enterprise',
      monitors: 25,
      status: 'active',
      joinDate: '2023-12-10',
      lastActive: '5 minutes ago'
    },
    {
      id: 3,
      name: 'Mike Johnson',
      email: 'mike.j@startup.io',
      plan: 'Free',
      monitors: 2,
      status: 'inactive',
      joinDate: '2024-01-20',
      lastActive: '3 days ago'
    },
    {
      id: 4,
      name: 'Sarah Wilson',
      email: 'sarah.w@corp.com',
      plan: 'Professional',
      monitors: 12,
      status: 'active',
      joinDate: '2023-11-05',
      lastActive: '1 hour ago'
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlan = filterPlan === 'all' || user.plan.toLowerCase() === filterPlan;
    return matchesSearch && matchesPlan;
  });

  const getStatusColor = (status) => {
    return status === 'active' ? 'text-green-400' : 'text-red-400';
  };

  const getPlanColor = (plan) => {
    switch (plan.toLowerCase()) {
      case 'free':
        return 'bg-gray-500/20 text-gray-300';
      case 'professional':
        return 'bg-purple-500/20 text-purple-300';
      case 'enterprise':
        return 'bg-blue-500/20 text-blue-300';
      default:
        return 'bg-gray-500/20 text-gray-300';
    }
  };

  return (
    <div className="min-h-screen">
      <AdminNavbar />
      
      <div className="pt-20 px-4 pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex justify-between items-center mb-8"
          >
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">User Management</h1>
              <p className="text-white/70">
                Manage and monitor all platform users
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-white">{users.length}</p>
              <p className="text-white/70 text-sm">Total Users</p>
            </div>
          </motion.div>

          {/* Search and Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl p-6 mb-8"
          >
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-3 text-white/50" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                />
              </div>
              <div className="relative">
                <SafeIcon icon={FiFilter} className="absolute left-3 top-3 text-white/50" />
                <select
                  value={filterPlan}
                  onChange={(e) => setFilterPlan(e.target.value)}
                  className="pl-10 pr-8 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent"
                >
                  <option value="all">All Plans</option>
                  <option value="free">Free</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
            </div>
          </motion.div>

          {/* Users Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-2xl overflow-hidden"
          >
            <div className="p-6 border-b border-white/20">
              <h2 className="text-xl font-semibold text-white">All Users ({filteredUsers.length})</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="text-left py-4 px-6 text-white/80 font-medium">User</th>
                    <th className="text-left py-4 px-6 text-white/80 font-medium">Plan</th>
                    <th className="text-left py-4 px-6 text-white/80 font-medium">Monitors</th>
                    <th className="text-left py-4 px-6 text-white/80 font-medium">Status</th>
                    <th className="text-left py-4 px-6 text-white/80 font-medium">Last Active</th>
                    <th className="text-left py-4 px-6 text-white/80 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-white/5 transition-colors">
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-xl flex items-center justify-center">
                            <SafeIcon icon={FiUsers} className="text-white" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            <p className="text-white/60 text-sm">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPlanColor(user.plan)}`}>
                          {user.plan}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-white">{user.monitors}</span>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`font-medium ${getStatusColor(user.status)}`}>
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span className="text-white/70">{user.lastActive}</span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center space-x-2">
                          <button className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                            <SafeIcon icon={FiMail} className="text-white/70 hover:text-white" />
                          </button>
                          <button className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors">
                            <SafeIcon icon={FiEdit} className="text-white/70 hover:text-white" />
                          </button>
                          <button className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors">
                            <SafeIcon icon={FiTrash2} className="text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;